/**
 * PricingGrid (T8.1a F4) — wizualny grid {modules} × {currencies}.
 *
 * Each cell either:
 *  - shows two inputs (priceMonthly + priceYearly) for the existing pricing row, or
 *  - shows "+ Dodaj cenę" trigger to create a new row.
 *
 * Edit-on-blur:
 *  - Per-cell local state (serverValue + localValue).
 *  - On blur, if changed → fire PATCH with diff-only body.
 *  - On 200: update serverValue (optimistic-then-confirm).
 *  - On 422: revert localValue to serverValue, set inline error.
 *
 * Prices are stored in grosze (integer); displayed in major units (PLN/EUR/USD).
 *
 * Plan: docs/phases/PHASE_8_PLAN.md §T8.1a + plan rev 2 (D8 — 5×3 grid).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { groupByArea } from '@/lib/area-groups';

import { Button } from './Button';

const CURRENCIES = ['PLN', 'EUR', 'USD'] as const;
type Currency = (typeof CURRENCIES)[number];

export interface PricingGridModule {
  id: string;
  code: string;
  name: string;
  areaId: string | null;
  areaName: string | null;
  areaSortOrder: number | null;
}

export interface PricingGridRow {
  id: string;
  moduleId: string;
  pricingModel: string;
  priceMonthly: number; // grosze
  priceYearly: number | null; // grosze
  currencyCode: string;
  isActive: boolean | null;
}

export interface PricingGridProps {
  modules: PricingGridModule[];
  pricing: PricingGridRow[];
}

interface ApiErrorShape {
  response?: { status?: number; data?: { error?: string } };
}

function groszToMajor(g: number | null | undefined): string {
  if (g === null || g === undefined) return '';
  return (g / 100).toString();
}

function majorToGrosz(s: string): number | null {
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

interface CellState {
  serverMonthly: string;
  serverYearly: string;
  localMonthly: string;
  localYearly: string;
  error: string | null;
  busy: boolean;
  // Set true after user clicks "+ Dodaj cenę" on an empty cell, so inputs
  // remain visible while values stay empty (avoids accidental priceMonthly=0 POST).
  revealed: boolean;
}

function makeKey(moduleId: string, currency: Currency): string {
  return `${moduleId}-${currency}`;
}

export function PricingGrid({ modules, pricing }: PricingGridProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();

  // Build initial state Map<key, {row | undefined, cellState}>.
  const initialState = (): Record<string, CellState> => {
    const acc: Record<string, CellState> = {};
    for (const m of modules) {
      for (const c of CURRENCIES) {
        const row = pricing.find((p) => p.moduleId === m.id && p.currencyCode === c);
        const k = makeKey(m.id, c);
        acc[k] = {
          serverMonthly: groszToMajor(row?.priceMonthly ?? null),
          serverYearly: groszToMajor(row?.priceYearly ?? null),
          localMonthly: groszToMajor(row?.priceMonthly ?? null),
          localYearly: groszToMajor(row?.priceYearly ?? null),
          error: null,
          busy: false,
          revealed: false,
        };
      }
    }
    return acc;
  };

  const [cells, setCells] = useState<Record<string, CellState>>(initialState);

  // Grupowanie po Obszarze — ta sama oś co Katalog modułów (wspólny helper).
  const groups = groupByArea(modules);

  function getRow(moduleId: string, currency: Currency): PricingGridRow | undefined {
    return pricing.find((p) => p.moduleId === moduleId && p.currencyCode === currency);
  }

  async function handleBlur(
    moduleId: string,
    currency: Currency,
    field: 'monthly' | 'yearly',
  ): Promise<void> {
    const key = makeKey(moduleId, currency);
    const cell = cells[key];
    if (!cell) return;

    const row = getRow(moduleId, currency);
    const localKey = field === 'monthly' ? 'localMonthly' : 'localYearly';
    const serverKey = field === 'monthly' ? 'serverMonthly' : 'serverYearly';
    const localVal = cell[localKey];
    const serverVal = cell[serverKey];
    if (localVal === serverVal) return; // no change

    const newGrosz = localVal.length > 0 ? majorToGrosz(localVal) : null;
    if (newGrosz === null && field === 'monthly') {
      // Monthly required (NOT NULL) — invalid empty.
      setCells((s) => ({
        ...s,
        [key]: { ...s[key]!, error: t('admin.modulePricing.validation.monthlyRequired') },
      }));
      return;
    }

    setCells((s) => ({ ...s, [key]: { ...s[key]!, busy: true, error: null } }));

    try {
      if (row) {
        const body: Record<string, number | null> = {};
        if (field === 'monthly') body.priceMonthly = newGrosz!;
        else body.priceYearly = newGrosz;
        await api.patch(`/admin/module-pricing/${row.id}`, body);
      } else {
        // Create new row. priceMonthly is NOT NULL in schema — refuse to POST
        // until the user has typed a value, otherwise blurring yearly first
        // would silently create a freemium-monthly row.
        const monthlyGrosz = cell.localMonthly.length > 0 ? majorToGrosz(cell.localMonthly) : null;
        if (monthlyGrosz === null) {
          setCells((s) => ({
            ...s,
            [key]: {
              ...s[key]!,
              busy: false,
              error: t('admin.modulePricing.validation.monthlyRequired'),
            },
          }));
          return;
        }
        await api.post('/admin/module-pricing', {
          moduleId,
          currencyCode: currency,
          pricingModel: 'per_tenant',
          priceMonthly: monthlyGrosz,
          priceYearly: field === 'yearly' ? newGrosz : null,
        });
      }
      // Confirm: shift local → server.
      setCells((s) => ({
        ...s,
        [key]: {
          ...s[key]!,
          [serverKey]: localVal,
          busy: false,
          error: null,
        },
      }));
      void queryClient.invalidateQueries({ queryKey: ['admin', 'module-pricing'] });
    } catch (err) {
      const e = err as ApiErrorShape;
      const errorMsg =
        e.response?.status === 422
          ? t('admin.modulePricing.validation.invalid')
          : t('admin.modulePricing.toasts.cellSaveError');
      // Revert local to server.
      setCells((s) => ({
        ...s,
        [key]: {
          ...s[key]!,
          [localKey]: s[key]![serverKey],
          busy: false,
          error: errorMsg,
        },
      }));
      toast.show({ variant: 'error', title: errorMsg });
    }
  }

  function setLocal(
    moduleId: string,
    currency: Currency,
    field: 'monthly' | 'yearly',
    val: string,
  ) {
    const key = makeKey(moduleId, currency);
    const localKey = field === 'monthly' ? 'localMonthly' : 'localYearly';
    setCells((s) => ({ ...s, [key]: { ...s[key]!, [localKey]: val } }));
  }

  function startAdd(moduleId: string, currency: Currency) {
    const key = makeKey(moduleId, currency);
    // Reveal inputs without pre-filling values — `revealed` keeps inputs
    // visible while empty values prevent an accidental priceMonthly=0 POST.
    setCells((s) => ({
      ...s,
      [key]: { ...s[key]!, revealed: true },
    }));
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border-secondary">
            <th className="px-3 py-2 text-left">{t('admin.modulePricing.columns.module')}</th>
            {CURRENCIES.map((c) => (
              <th key={c} className="px-3 py-2 text-left">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        {groups.map((group) => (
          <tbody key={group.areaId ?? '__no_area__'}>
            <tr className="border-t border-border-secondary bg-bg-secondary/50">
              <td
                colSpan={1 + CURRENCIES.length}
                className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-text-secondary"
                data-testid="pricing-area-group-header"
              >
                {group.areaId === null
                  ? t('admin.modules.groups.noArea')
                  : (group.areaName ?? group.areaId)}
              </td>
            </tr>
            {group.items.map((m) => (
              <tr key={m.id} className="border-b border-border-secondary">
                <td className="px-3 py-2 font-medium">{m.name}</td>
                {CURRENCIES.map((c) => {
                  const key = makeKey(m.id, c);
                  const cell = cells[key]!;
                  const row = getRow(m.id, c);
                  const hasValue =
                    cell.localMonthly.length > 0 ||
                    cell.localYearly.length > 0 ||
                    Boolean(row) ||
                    cell.revealed;

                  if (!hasValue) {
                    return (
                      <td key={c} className="px-3 py-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`pricing-cell-${m.id}-${c}-add`}
                          onClick={() => startAdd(m.id, c)}
                        >
                          {t('admin.modulePricing.addCellLabel')}
                        </Button>
                      </td>
                    );
                  }

                  return (
                    <td key={c} className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <input
                          data-testid={`pricing-cell-${m.id}-${c}-monthly`}
                          type="number"
                          step="0.01"
                          min="0"
                          className="w-24 rounded border border-border-primary bg-bg-primary px-2 py-1"
                          value={cell.localMonthly}
                          onChange={(e) => setLocal(m.id, c, 'monthly', e.target.value)}
                          onBlur={() => void handleBlur(m.id, c, 'monthly')}
                          disabled={cell.busy}
                          aria-label={`${m.code} ${c} monthly`}
                        />
                        <input
                          data-testid={`pricing-cell-${m.id}-${c}-yearly`}
                          type="number"
                          step="0.01"
                          min="0"
                          className="w-24 rounded border border-border-primary bg-bg-primary px-2 py-1"
                          value={cell.localYearly}
                          onChange={(e) => setLocal(m.id, c, 'yearly', e.target.value)}
                          onBlur={() => void handleBlur(m.id, c, 'yearly')}
                          disabled={cell.busy}
                          aria-label={`${m.code} ${c} yearly`}
                        />
                        {cell.error && <span className="text-xs text-red-600">{cell.error}</span>}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}
